/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from "react-redux";
import moment from 'moment-timezone';

// Import sub-components
import { ClearSelectionButton, DateInfoBox, SelectedTimeSummary, TimeSlotItem, TimeSlotLegend, TimeSlotTips } from './helpers/TimeSlotHelpers';
import { MinimumDurationPopup } from './modals/MinimumDurationPopup';
import { ProductInfoModal } from './modals/ProductInfoModal';
import { ProductHeader } from './sections/ProductHeader';
import PaymentAccessModal from './modals/PaymentAccessModal';
import { usePaymentAccess } from '../../../../../hooks/usePaymentAccess';

const ProductResultCard = ({ product, onSelect, searchCriteria, onUpdatePeople, onModifySearch, companyTimezone }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [showMinimumInfo, setShowMinimumInfo] = useState(false);
  const [showProductInfo, setShowProductInfo] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const timelineRef = useRef(null);

  // ── Touch-drag state ──────────────────────────────────────────────────────
  const touchDragging = useRef(false);
  const touchDragStart = useRef(null);
  // ─────────────────────────────────────────────────────────────────────────

  const companyState = useSelector((state) => state?.companyReducer?.company) || {};
  const { isPaymentAllowed, contactPerson } = usePaymentAccess();
  const timezone = companyTimezone || companyState?.operatingLocation?.zoneId || 'America/New_York';

  // ---------------------------------------------------------------------------
  // Timezone helpers
  // ---------------------------------------------------------------------------

  const getCurrentTimeInCompanyTimezone = useCallback(() => {
    try {
      return moment.tz(timezone);
    } catch {
      return moment();
    }
  }, [timezone]);

  const getDateInCompanyTimezone = useCallback((dateString) => {
    try {
      return moment.tz(dateString, 'YYYY-MM-DD', timezone);
    } catch {
      return moment(dateString);
    }
  }, [timezone]);

  // ---------------------------------------------------------------------------
  // Time formatting helpers
  // ---------------------------------------------------------------------------

  const formatTime12Hour = useCallback((time24) => {
    if (!time24) return 'Invalid time';
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  }, []);

  const formatTimeRange = useCallback((startTime, endTime) => {
    if (!startTime || !endTime) return 'Invalid time';
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startPeriod = startHour >= 12 ? 'PM' : 'AM';
    const endPeriod = endHour >= 12 ? 'PM' : 'AM';
    const start12 = startHour % 12 || 12;
    const end12 = endHour % 12 || 12;

    // Format with minutes (omit :00 for whole hours, show :30 etc. otherwise)
    const startStr = startMin === 0 ? `${start12}` : `${start12}:${startMin.toString().padStart(2, '0')}`;
    const endStr = endMin === 0 ? `${end12}` : `${end12}:${endMin.toString().padStart(2, '0')}`;

    if (startPeriod === endPeriod) {
      return `${startStr} - ${endStr} ${endPeriod}`;
    }
    return `${startStr} ${startPeriod} - ${endStr} ${endPeriod}`;
  }, []);

  const isPastTimeSlot = useCallback((timeString) => {
    try {
      const nowInCompanyTz = getCurrentTimeInCompanyTimezone();
      const selectedDateInCompanyTz = getDateInCompanyTimezone(searchCriteria.date);
      const isTodayInCompanyTz = selectedDateInCompanyTz.isSame(nowInCompanyTz, 'day');

      if (!isTodayInCompanyTz) return false;

      const [slotHour, slotMinute] = timeString.split(':').map(Number);
      const slotTimeInCompanyTz = selectedDateInCompanyTz
        .clone()
        .hour(slotHour)
        .minute(slotMinute)
        .second(0)
        .millisecond(0);

      return slotTimeInCompanyTz.isBefore(nowInCompanyTz);
    } catch {
      return false;
    }
  }, [getCurrentTimeInCompanyTimezone, getDateInCompanyTimezone, searchCriteria.date]);

  // ---------------------------------------------------------------------------
  // Normalise a time string that may be either "HH:mm" or "HH:mm:ss"
  // Returns "HH:mm"
  // ---------------------------------------------------------------------------
  const normaliseTime = useCallback((rawTime) => {
    if (!rawTime) return null;
    return rawTime.substring(0, 5);
  }, []);

  const getSlotDurationInMinutes = (slot) => {
    if (slot?.intervalInMinutes) {
      return slot.intervalInMinutes;
    }

    if (slot?.time && slot?.endTime) {
      const [startHour, startMinute] = slot.time.split(':').map(Number);
      const [endHour, endMinute] = slot.endTime.split(':').map(Number);
      return (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
    }

    return companyState?.workingSchedule?.slotIntervalInMinutes || 60;
  };

  // ---------------------------------------------------------------------------
  // Generate time slots
  // ---------------------------------------------------------------------------
  const generateTimeSlots = useCallback(() => {
    if (!Array.isArray(product.slots) || product.slots.length === 0) {
      return [];
    }

    return product.slots
      .map((apiSlot) => {
        const startTime = normaliseTime(
          apiSlot.startTime?.length > 8
            ? apiSlot.startTime.substring(11, 16)
            : apiSlot.startTime
        );
        const endTime = normaliseTime(
          apiSlot.endTime?.length > 8
            ? apiSlot.endTime.substring(11, 16)
            : apiSlot.endTime
        );

        if (!startTime || !endTime) {
          return null;
        }

        const status = apiSlot.status || 'OPEN';
        const isBlocked = status === 'BLOCKED' || status === 'NOT_AVAILABLE';
        const isBooked = status === 'RESERVED' || status === 'BOOKED';
        const isPast = isPastTimeSlot(startTime);

        return {
          time: startTime,
          endTime,
          intervalInMinutes:
            apiSlot.intervalInMinutes ||
            companyState?.workingSchedule?.slotIntervalInMinutes ||
            60,
          displayLabel: `${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)}`,
          rangeLabel: formatTimeRange(startTime, endTime),
          booked: isBooked,
          unavailable: isBlocked,
          past: isPast,
          status,
          selectable: !isBooked && !isBlocked && !isPast,
          selected: false
        };
      })
      .filter(Boolean)
      .sort((leftSlot, rightSlot) => leftSlot.time.localeCompare(rightSlot.time));
  }, [
    companyState?.workingSchedule?.slotIntervalInMinutes,
    formatTime12Hour,
    formatTimeRange,
    isPastTimeSlot,
    normaliseTime,
    product.slots,
  ]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setTimeSlots(generateTimeSlots());
    setSelectedSlots([]);
    setShowMinimumInfo(false);
  }, [generateTimeSlots, product.primaryId]);

  // ---------------------------------------------------------------------------
  // Validation – minimum booking duration is 1 hour (60 minutes)
  // ---------------------------------------------------------------------------
  const validateMinimumBookingDuration = (slots) => {
    const totalMinutes = slots.reduce(
      (sum, slot) => sum + getSlotDurationInMinutes(slot),
      0
    );
    const minimumMinutes = 60; // 1-hour minimum

    if (totalMinutes < minimumMinutes) {
      const baseSlotDuration = getSlotDurationInMinutes(slots[0]);
      const requiredSlots = Math.ceil(minimumMinutes / baseSlotDuration);
      return { isValid: false, requiredSlots };
    }
    return { isValid: true, requiredSlots: 0 };
  };

  // ---------------------------------------------------------------------------
  // Shared selection update helper (used by both mouse and touch paths)
  // ---------------------------------------------------------------------------
  const updateSelectedSlots = (slots) => {
    const selected = slots.filter((slot) => slot.selected);
    setSelectedSlots(selected);

    if (selected.length > 0) {
      const validation = validateMinimumBookingDuration(selected);
      setShowMinimumInfo(!validation.isValid);
    } else {
      setShowMinimumInfo(false);
    }
  };

  // Shared range-selection logic for both mouse and touch
  const applyDragRange = useCallback((startIdx, endIdx, slots) => {
    const start = Math.min(startIdx, endIdx);
    const end = Math.max(startIdx, endIdx);

    // Stop at first unselectable slot within range (same as mouse logic)
    let validEnd = start;
    for (let i = start; i <= end; i++) {
      if (!slots[i].selectable) break;
      validEnd = i;
    }

    return slots.map((slot, i) => ({
      ...slot,
      selected: i >= start && i <= validEnd && slot.selectable
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Mouse / drag handlers (unchanged logic)
  // ---------------------------------------------------------------------------

  const handleMouseDown = (index) => {
    if (!timeSlots[index].selectable) return;

    setIsDragging(true);
    setDragStart(index);

    const updatedSlots = timeSlots.map((slot, i) => ({
      ...slot,
      selected: i === index && slot.selectable
    }));

    setTimeSlots(updatedSlots);
    updateSelectedSlots(updatedSlots);
  };

  const handleMouseEnter = (index) => {
    setHoveredSlot(index);

    if (!isDragging || !timeSlots[index].selectable) return;

    const start = Math.min(dragStart, index);
    const end = Math.max(dragStart, index);

    const hasUnselectableSlot = timeSlots.slice(start, end + 1).some((slot) => !slot.selectable);
    if (hasUnselectableSlot) {
      let validEnd = start;
      for (let i = start; i <= end; i++) {
        if (!timeSlots[i].selectable) break;
        validEnd = i;
      }

      const updatedSlots = timeSlots.map((slot, i) => ({
        ...slot,
        selected: i >= start && i <= validEnd && slot.selectable
      }));

      setTimeSlots(updatedSlots);
      updateSelectedSlots(updatedSlots);
      return;
    }

    const updatedSlots = timeSlots.map((slot, i) => ({
      ...slot,
      selected: i >= start && i <= end && slot.selectable
    }));

    setTimeSlots(updatedSlots);
    updateSelectedSlots(updatedSlots);
  };

  const handleMouseLeave = () => setHoveredSlot(null);

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  // ---------------------------------------------------------------------------
  // Touch handlers – mirrors mouse drag logic for mobile/tablet
  // ---------------------------------------------------------------------------

  /**
   * Map a Touch clientX/clientY to the slot index that lies under that point.
   * Returns -1 if the point is outside the timeline container.
   */
  // ---------------------------------------------------------------------------
  // Capacity guard – placed AFTER all hooks to satisfy rules-of-hooks
  // ---------------------------------------------------------------------------
  const exceedsCapacity = product.capacity && searchCriteria.people > product.capacity;
  if (exceedsCapacity) return null;

  // ---------------------------------------------------------------------------
  // Display helpers
  // ---------------------------------------------------------------------------

  const clearSelection = () => {
    const updatedSlots = timeSlots.map((slot) => ({ ...slot, selected: false }));
    setTimeSlots(updatedSlots);
    setSelectedSlots([]);
    setShowMinimumInfo(false);
  };

  const formatSelectedTimeRange = () => {
    if (selectedSlots.length === 0) return 'No selection';

    const startSlot = selectedSlots[0];
    const endSlot = selectedSlots[selectedSlots.length - 1];

    if (!startSlot || !endSlot) return 'Invalid selection';

    return `${formatTime12Hour(startSlot.time)} - ${formatTime12Hour(endSlot.endTime)}`;
  };

  const calculateTotalPrice = () => {
    const selectedHours = selectedSlots.reduce(
      (sum, slot) => sum + (getSlotDurationInMinutes(slot) / 60),
      0
    );
    return ((product.price?.priceAmt || 0) * selectedHours).toFixed(2);
  };

  const getDurationLabel = () => {
    if (selectedSlots.length === 0) return '';
    const hours = selectedSlots.reduce(
      (sum, slot) => sum + (getSlotDurationInMinutes(slot) / 60),
      0
    );
    return hours === 1 ? '1 hour' : `${hours} hours`;
  };

  const stripHtml = (html = '') => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // ---------------------------------------------------------------------------
  // Book handler – includes payment access check
  // ---------------------------------------------------------------------------

  const handleSelectProduct = () => {
    if (product.capacity && searchCriteria.people > product.capacity) return;

    const validation = validateMinimumBookingDuration(selectedSlots);
    if (!validation.isValid) {
      setShowMinimumInfo(true);
      return;
    }

    if (selectedSlots.length === 0) return;

    if (!isPaymentAllowed) {
      setShowPaymentModal(true);
      return;
    }

    const startSlot = selectedSlots[0];
    const endSlot = selectedSlots[selectedSlots.length - 1];

    const selectedTime = {
      start: startSlot.time,
      end: endSlot.endTime,
      duration: selectedSlots.reduce(
        (sum, slot) => sum + (getSlotDurationInMinutes(slot) / 60),
        0
      ),
      durationType: 'HOUR'
    };

    onSelect({
      ...product,
      selectedTime,
      totalPrice: calculateTotalPrice()
    });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const slotInterval = getSlotDurationInMinutes(timeSlots[0]);
  const requiredSlots = Math.ceil(60 / slotInterval); // 1-hour minimum

  const indexFromPoint = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return -1;
    const wrapper = el.closest('[data-slot-index]');
    if (!wrapper) return -1;
    return parseInt(wrapper.getAttribute('data-slot-index'), 10);
  };

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const { clientX, clientY } = e.touches[0];
    const index = indexFromPoint(clientX, clientY);
    if (index < 0 || !timeSlots[index]?.selectable) return;
    e.preventDefault();
    touchDragging.current = true;
    touchDragStart.current = index;
    setIsDragging(true);
    setDragStart(index);
    const updatedSlots = timeSlots.map((slot, i) => ({
      ...slot,
      selected: i === index && slot.selectable
    }));
    setTimeSlots(updatedSlots);
    updateSelectedSlots(updatedSlots);
  };

  const onTouchMove = (e) => {
    if (!touchDragging.current || e.touches.length !== 1) return;
    e.preventDefault();
    const { clientX, clientY } = e.touches[0];
    const index = indexFromPoint(clientX, clientY);
    if (index < 0) return;
    setHoveredSlot(index);
    const updatedSlots = applyDragRange(touchDragStart.current, index, timeSlots);
    setTimeSlots(updatedSlots);
    updateSelectedSlots(updatedSlots);
  };

  const onTouchEnd = () => {
    touchDragging.current = false;
    touchDragStart.current = null;
    setIsDragging(false);
    setDragStart(null);
    setHoveredSlot(null);
  };

  const handleMobileTap = (index) => {
    if (!timeSlots[index]?.selectable) return;

    if (selectedSlots.length === 0) {
      const updatedSlots = timeSlots.map((slot, i) => ({
        ...slot,
        selected: i === index && slot.selectable
      }));
      setTimeSlots(updatedSlots);
      updateSelectedSlots(updatedSlots);
      return;
    }

    const selectedIndices = timeSlots
      .map((s, i) => (s.selected ? i : -1))
      .filter((i) => i >= 0);
    const currentStart = selectedIndices[0];
    const currentEnd = selectedIndices[selectedIndices.length - 1];

    // Tap inside selection → deselect all
    if (index >= currentStart && index <= currentEnd) {
      clearSelection();
      return;
    }

    // Extend range to tapped slot
    const newStart = Math.min(currentStart, index);
    const newEnd = Math.max(currentEnd, index);
    const updatedSlots = applyDragRange(newStart, newEnd, timeSlots);
    setTimeSlots(updatedSlots);
    updateSelectedSlots(updatedSlots);
  };

  // Desktop: original horizontal grid (mouse + touch-drag)
  const renderDesktopSlots = () => (
    <div
      ref={timelineRef}
      className="grid auto-cols-fr grid-flow-col gap-0 select-none"
      style={{ touchAction: 'none' }}
      onMouseLeave={() => { handleMouseUp(); handleMouseLeave(); }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      {timeSlots.map((slot, index) => (
        <div key={index} data-slot-index={index} style={{ display: 'contents' }}>
          <TimeSlotItem
            slot={slot}
            index={index}
            isSelected={slot.selected}
            isBooked={slot.booked}
            isBlocked={slot.unavailable}
            isPast={slot.past}
            isSelectable={slot.selectable}
            isHovered={hoveredSlot === index}
            isDragging={isDragging}
            handleMouseDown={handleMouseDown}
            handleMouseEnter={handleMouseEnter}
            handleMouseUp={handleMouseUp}
          />
        </div>
      ))}
    </div>
  );

  // Mobile/tablet: vertical tap-to-select list
  const renderMobileSlots = () => (
    <div className="flex flex-col divide-y divide-slate-100 select-none">
      {timeSlots.map((slot, index) => {
        const isSelected = slot.selected;
        const isBooked = slot.booked;
        const isBlocked = slot.unavailable;
        const isPast = slot.past;
        const isSelectable = slot.selectable;

        let rowBg = 'bg-white hover:bg-slate-50';
        let timeColor = 'text-slate-700';
        let badgeEl = <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Available</span>;

        if (isPast) {
          rowBg = 'bg-slate-50';
          timeColor = 'text-slate-400 line-through';
          badgeEl = <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Past</span>;
        } else if (isBlocked) {
          rowBg = 'bg-slate-200';
          timeColor = 'text-slate-600';
          badgeEl = <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">It's not available</span>;
        } else if (isBooked) {
          rowBg = 'bg-red-50';
          timeColor = 'text-red-400';
          badgeEl = <span className="text-xs font-medium text-red-500 bg-red-100 px-2 py-0.5 rounded-full">Reserved</span>;
        } else if (isSelected) {
          rowBg = 'bg-blue-600 hover:bg-blue-700';
          timeColor = 'text-white font-semibold';
          badgeEl = <span className="text-xs font-medium text-blue-100 bg-blue-500 px-2 py-0.5 rounded-full">✓ Selected</span>;
        }

        return (
          <button
            key={index}
            type="button"
            data-slot-index={index}
            disabled={!isSelectable}
            onClick={() => handleMobileTap(index)}
            className={`
              w-full flex items-center justify-between px-4 py-3.5
              transition-colors duration-150 text-left
              ${rowBg}
              ${!isSelectable ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            `}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="flex items-center gap-3">
              {/* Checkbox circle */}
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                transition-all duration-150
                ${isSelected
                  ? 'border-white bg-white'
                  : isBooked || isPast
                    ? 'border-slate-300'
                    : 'border-blue-400'}
              `}>
                {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
              </div>
              <span className={`text-sm ${timeColor}`}>{slot.displayLabel}</span>
            </div>
            {badgeEl}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 mb-4 sm:mb-6 overflow-visible relative w-full">
      {/* ── Modals ── */}
      <ProductInfoModal
        showProductInfo={showProductInfo}
        setShowProductInfo={setShowProductInfo}
        product={product}
        companyState={companyState}
        stripHtml={stripHtml}
      />

      <MinimumDurationPopup
        showMinimumInfo={showMinimumInfo}
        setShowMinimumInfo={setShowMinimumInfo}
        requiredSlots={requiredSlots}
      />

      <PaymentAccessModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        contactPerson={contactPerson}
        companyName={companyState?.name}
      />

      {/* ── Header ── */}
      <ProductHeader
        product={product}
        companyState={companyState}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        selectedSlots={selectedSlots}
        formatSelectedTimeRange={formatSelectedTimeRange}
        setShowProductInfo={setShowProductInfo}
      />

      {/* ── Expanded Content ── */}
      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50">
          <div className="p-4 sm:p-5 lg:p-6">

            {/* Date info + clear button row */}
            <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-3 mb-4 sm:mb-5">
              <DateInfoBox
                searchCriteria={searchCriteria}
                companyState={companyState}
              />

              {selectedSlots.length > 0 && (
                <div className="self-end xs:self-auto">
                  <ClearSelectionButton clearSelection={clearSelection} />
                </div>
              )}
            </div>

            {/* ── Mobile / tablet: vertical tap-to-select list (hidden on lg+) ── */}
            <div className="block lg:hidden bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Tap to select · tap again to extend
                </span>
                {selectedSlots.length > 0 && (
                  <span className="text-xs font-bold text-blue-600">
                    {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              {renderMobileSlots()}
            </div>

            {/* ── Desktop: original horizontal drag grid (hidden below lg) ── */}
            <div className="hidden lg:block bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              {renderDesktopSlots()}
            </div>

            <TimeSlotLegend />

            {selectedSlots.length > 0 && (
              <SelectedTimeSummary
                formatSelectedTimeRange={formatSelectedTimeRange}
                getDurationLabel={getDurationLabel}
                product={product}
                calculateTotalPrice={calculateTotalPrice}
                showMinimumInfo={showMinimumInfo}
                handleSelectProduct={handleSelectProduct}
              />
            )}

            <TimeSlotTips
              slotInterval={slotInterval}
              product={product}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductResultCard;
